import React, { useMemo } from "react";
import type {
    JSX,
    InputHTMLAttributes,
    TextareaHTMLAttributes,
    ElementType,
    PropsWithChildren,
    CSSProperties,
    FC,
} from "react";

import cn from "classnames";

import { InputContainer } from "./components/InputContainer/InputContainer";
import {
    InputContainerProvider,
    useInputContainerStore,
} from "./store/inputContainerStore";
import { CoreInputTextProvider, useCoreInputTextStore } from "./store/store";
import { CORE_INPUT_TEXT_DISPLAY_NAME } from "./utils/core";
import { getChildrenRecord } from "../../../utils/react";
import { type BoxComponentWithDefault, boxForwardRef } from "../Box/Box";
import { Gap } from "../Gap/Gap";

import type { ICoreInputTextProps } from "./types";

import classes from "./CoreInputText.styl";

type InputProps =
    | ICoreInputTextProps<InputHTMLAttributes<HTMLInputElement>>
    | ICoreInputTextProps<TextareaHTMLAttributes<HTMLTextAreaElement>>;

export type BoxInputType = BoxComponentWithDefault<"input", InputProps>;

export const CoreInputText = boxForwardRef<BoxInputType>(
    ({ children, state = "enabled", ...props }, ref) => {
        return (
            // eslint-disable-next-line ok-custom-rules/check-attribute
            <CoreInputTextProvider state={state}>
                <CoreInputTextContent {...props} ref={ref}>
                    {children}
                </CoreInputTextContent>
            </CoreInputTextProvider>
        );
    },
);

interface IDefaultInputContainerProps extends PropsWithChildren {
    Input: ElementType;
    prefix: string;
    inputContainerClassName?: string;
    inputContainerStyle?: CSSProperties;
    inputProps?: InputProps;
}

const DefaultInputContainer: FC<IDefaultInputContainerProps> = ({
    Input,
    prefix,
    ...props
}) => {
    const { inputId } = useCoreInputTextStore();
    const { ref, inputProps, inputChildren, style, className } =
        useInputContainerStore();

    return (
        <label htmlFor={inputId} className={classes["input-wrapper"]}>
            <InputContainer
                as={Input}
                {...inputProps}
                {...props}
                prefix={prefix}
                ref={ref}
                style={style}
                className={className}
            >
                {inputChildren}
            </InputContainer>
        </label>
    );
};
DefaultInputContainer.displayName = "DefaultInputContainer";

const CoreInputTextContent = boxForwardRef<BoxInputType>(
    (
        {
            as: Input = "input",
            view = "primary",
            horizontal = false,
            prefix,
            children,
            customColorsStyles,
            customVarsStyles,
            inputContainerClassName,
            inputContainerStyle,
            "data-tsid": tsid,
            ...inputProps
        },
        ref,
    ): JSX.Element => {
        const { state, inputId } = useCoreInputTextStore();
        const {
            Label,
            Hint,
            Bottom,
            InputContainer = (
                <DefaultInputContainer Input={Input} prefix={prefix}>
                    {children}
                </DefaultInputContainer>
            ),
            ...inputChildren
        } = useMemo(
            () => getChildrenRecord(children, prefix, {}),
            [children, prefix],
        );

        const wrapperClasses = (() => {
            const result = {} as cn.Mapping;
            if (view && view !== "primary") {
                result[classes[`__view-${view}`]] = true;
            }
            if (state === "disabled") {
                result[classes[`__state-${state}`]] = true;
            }
            return cn(
                classes["wrapper"],
                customColorsStyles,
                customVarsStyles,
                result,
            );
        })();

        const wrapperContentClasses = (() => {
            const result = {
                [classes["__horizontal"]]: horizontal,
            };
            return cn(classes["wrapper-content"], result);
        })();

        return (
            <Gap
                className={wrapperClasses}
                contentClassName={wrapperContentClasses}
                data-tsid={tsid}
            >
                {Label && (
                    <label
                        htmlFor={inputId}
                        className={classes["label-wrapper"]}
                    >
                        {Label}
                    </label>
                )}
                <Gap
                    className={classes["control-wrapper"]}
                    contentClassName={classes["control"]}
                >
                    <InputContainerProvider
                        ref={ref}
                        className={inputContainerClassName}
                        style={inputContainerStyle}
                        inputChildren={Object.values(inputChildren)}
                        inputProps={inputProps}
                    >
                        {InputContainer}
                    </InputContainerProvider>
                    {Hint}
                    {Bottom}
                </Gap>
            </Gap>
        );
    },
);

CoreInputText.displayName = CORE_INPUT_TEXT_DISPLAY_NAME;

export default CoreInputText;
